import { useParams } from '@tanstack/react-router'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

export interface EmbeddedDocument {
  id: string
  type: 'report' | 'system_document' | 'knowledge_document'
  name: string
  displayTitle: string
}

export const useEmbeddedDocuments = () => {
  const params = useParams({ strict: false })
  const siteId = params.siteID

  const { entries, isLoading } = useRolodexEntries(
    siteId
      ? {
          type: [
            RolodexEntryType.REPORT,
            RolodexEntryType.SYSTEM_DOCUMENT,
            RolodexEntryType.KNOWLEDGE_DOCUMENT,
          ],
          tags: { site: siteId },
          limit: 10000,
        }
      : undefined
  )

  const documents = useMemo((): EmbeddedDocument[] => {
    if (!entries || entries.length === 0) return []

    return entries
      .filter((entry) => {
        const fileType = entry.data?.type as string | undefined
        const visibleName = entry.data?.visible_name as string | undefined
        const isPdf =
          fileType?.toLowerCase().includes('pdf') ||
          visibleName?.toLowerCase().endsWith('.pdf')
        return isPdf
      })
      .map((entry) => {
        const filename =
          (entry.data?.visible_name as string) ||
          (entry.data?.filename as string) ||
          (entry.data?.name as string) ||
          entry.id

        const displayTitle =
          (entry.data?.document_title as string) ||
          (entry.data?.title as string) ||
          filename

        return {
          id: entry.id,
          type: entry.type as
            | 'report'
            | 'system_document'
            | 'knowledge_document',
          name: filename,
          displayTitle,
        }
      })
  }, [entries])

  return {
    documents,
    isLoading,
  }
}
